import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowRight, CreditCard, HelpCircle, BookOpen, Sparkles, FileText, Bell, MessageCircle, Cpu, LifeBuoy } from 'lucide-react';
import { cn } from '@/lib/utils';

const ALL_LINKS = [
  {
    id: 'pricing',
    title: "Pricing Plans",
    description: "Choose the perfect subscription for high-performance physics video creation and rendering.",
    href: "/plans",
    icon: CreditCard,
    accent: "text-cyan-600 bg-cyan-50 border-cyan-100",
    category: 'default',
  },
  {
    id: 'help',
    title: "Help Center",
    description: "Find quick answers to common questions about physics engines, video exports, and account setup.",
    href: "/help",
    icon: HelpCircle,
    accent: "text-purple-600 bg-purple-50 border-purple-100",
    category: 'default',
  },
  {
    id: 'tutorials',
    title: "Creator Tutorials",
    description: "Step-by-step guides on creating satisfying ball simulations and viral social media content.",
    href: "/tutorials",
    icon: BookOpen,
    accent: "text-emerald-600 bg-emerald-50 border-emerald-100",
    category: 'default',
  },
  {
    id: 'showcase',
    title: "Stunning Showcase",
    description: "Browse community-made simulations, trending visual configurations, and audio setups.",
    href: "/showcase",
    icon: Sparkles,
    accent: "text-amber-600 bg-amber-50 border-amber-100",
    category: 'default',
  },
  {
    id: 'blog',
    title: "Creator Blog",
    description: "Read insights on physics simulation trends, creator strategies, and platform updates.",
    href: "/blog",
    icon: FileText,
    accent: "text-rose-600 bg-rose-50 border-rose-100",
    category: 'extra',
  },
  {
    id: 'updates',
    title: "Latest Updates",
    description: "Stay up to date with new features, engine improvements, and community milestones.",
    href: "/updates",
    icon: Bell,
    accent: "text-indigo-600 bg-indigo-50 border-indigo-100",
    category: 'extra',
  },
  {
    id: 'faq',
    title: "FAQ",
    description: "Explore 100+ commonly asked questions about our business model, engine, and creator tools.",
    href: "/faq",
    icon: LifeBuoy,
    accent: "text-teal-600 bg-teal-50 border-teal-100",
    category: 'extra',
  },
  {
    id: 'discord',
    title: "Discord Community",
    description: "Join thousands of creators for beta testing, bug reports, feature requests, and discussions.",
    href: "https://discord.gg/cdwNFdbx",
    icon: MessageCircle,
    accent: "text-[#5865F2] bg-[#5865F2]/10 border-[#5865F2]/20",
    category: 'extra',
    isExternal: true,
  },
  {
    id: 'case-studies',
    title: "Case Studies",
    description: "See how top creators built viral channels using our physics simulation engine.",
    href: "/case-studies",
    icon: Cpu,
    accent: "text-orange-600 bg-orange-50 border-orange-100",
    category: 'extra',
  },
];

interface InternalLinksSectionProps {
  /** Show specific link IDs. If not provided, shows default 4 + discord. */
  show?: string[];
  /** Hide specific link IDs */
  hide?: string[];
  /** Custom title */
  title?: string;
  /** Custom subtitle */
  subtitle?: string;
  className?: string;
}

export const InternalLinksSection: React.FC<InternalLinksSectionProps> = ({
  show,
  hide,
  title = 'Explore Resources',
  subtitle = 'Maximize Your Creation Potential',
  className,
}) => {
  let links = ALL_LINKS;

  if (show) {
    links = ALL_LINKS.filter(l => show.includes(l.id));
  } else if (hide) {
    // Default behavior: show default 4 + discord, minus any hidden
    links = ALL_LINKS.filter(l => (l.category === 'default' || l.id === 'discord') && !hide.includes(l.id));
  } else {
    // Default: show default 4 + discord
    links = ALL_LINKS.filter(l => l.category === 'default' || l.id === 'discord');
  }

  return (
    <section className={cn('py-24 px-6 bg-zinc-50 border-t border-zinc-100 relative overflow-hidden', className)}>
      <div className="absolute inset-0 bg-gradient-to-b from-white/50 to-zinc-50 -z-10" />
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <h2 className="text-xs uppercase tracking-[0.4em] font-bold text-zinc-400">{title}</h2>
          <h3 className="text-4xl md:text-5xl font-display font-black tracking-tighter uppercase text-zinc-950">
            {subtitle.includes(' ') ? (
              <>
                {subtitle.split(' ').slice(0, -1).join(' ')}{' '}
                <span className="text-cyan-600">{subtitle.split(' ').slice(-1)}</span>
              </>
            ) : (
              <span className="text-cyan-600">{subtitle}</span>
            )}
          </h3>
          <p className="text-zinc-500 font-medium text-lg font-sans">
            Ready to dive deeper? Check out our guides, pricing options, community showcases, and help center.
          </p>
        </div>

        <div className={cn(
          'grid grid-cols-1 gap-6',
          links.length <= 3 ? 'md:grid-cols-3' : links.length <= 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-2 lg:grid-cols-3'
        )}>
          {links.map((link) => {
            const Icon = link.icon;
            const isExternal = 'isExternal' in link && link.isExternal;

            const cardContent = (
              <div className="space-y-6">
                <div className={`w-12 h-12 rounded-2xl border flex items-center justify-center ${link.accent}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <div className="space-y-3">
                  <h4 className="text-xl font-bold font-display text-zinc-900 group-hover:text-cyan-600 transition-colors uppercase tracking-tight">
                    {link.title}
                  </h4>
                  <p className="text-sm text-zinc-500 leading-relaxed font-sans font-medium">
                    {link.description}
                  </p>
                </div>
              </div>
            );

            const cardAction = (
              <div className="pt-8">
                <span className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-wider text-zinc-950 hover:text-cyan-600 transition-colors">
                  Explore More
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            );

            return (
              <motion.div
                key={link.id}
                whileHover={{ y: -6, scale: 1.02 }}
                transition={{ duration: 0.2 }}
                className="group relative bg-white border border-zinc-200 rounded-3xl p-8 flex flex-col justify-between hover:shadow-lg transition-all duration-300"
              >
                {isExternal ? (
                  <a href={link.href} target="_blank" rel="noopener noreferrer" className="flex flex-col justify-between h-full">
                    {cardContent}
                    {cardAction}
                  </a>
                ) : (
                  <Link to={link.href} className="flex flex-col justify-between h-full">
                    {cardContent}
                    {cardAction}
                  </Link>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default InternalLinksSection;
